//  ----------      START   IMPORT  BLOCK       ----------
import React, { useState } from 'react';
//  Widgets
import { Modal, Button, Form } from 'react-bootstrap';
// Context
import { useLanguage } from '../../../../../API/Context/LanguageContext';
//  Config
import { VocabButtons as btn_vocab } from '../../../../../ConfigFiles/multilingual/VocabButtons';
import VocabGeneralDisplay from '../../../../../ConfigFiles/multilingual/VocabGeneralDisplay';
import { VocabCivilStatus } from '../../../../../ConfigFiles/multilingual/VocabCivilStatus';
import VocabQualifications from '../../../../../ConfigFiles/multilingual/VocabQualifications';
import VocabReligions from '../../../../../ConfigFiles/multilingual/VocabReligions';
import VocabSexes from '../../../../../ConfigFiles/multilingual/VocabSexes';
// Types
import type { PartialGeneralInfoUpdate, PatientDetailsResponse } from '../../../../../Types/schema-helpers';
//  ----------      END   IMPORT  BLOCK       ----------

interface CustomModalProps {
  onSave: (payload: PartialGeneralInfoUpdate) => Promise<void>;
  onHide: () => void;
  initialData: PatientDetailsResponse['patient'];
}

export default function CustomModal({ onSave, onHide, initialData }: CustomModalProps) {
  const { lan } = useLanguage();

  const [formData, setFormData] = useState({
    height: initialData?.height ?? '',
    weight: initialData?.weight ?? '',
    marital_status: initialData?.marital_status ?? '',
    education: initialData?.education ?? '',
    occupation: initialData?.occupation ?? '',
    religion: initialData?.religion ?? '',
    sex: initialData?.current_sex ?? '',
  });

  const handleChange = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = () => {
    // Asserting types as any allows TypeScript to accept numeric literals into the specific union types
    const payload: PartialGeneralInfoUpdate = {
      height: formData.height !== '' ? Number(formData.height) : null,
      weight: formData.weight !== '' ? Number(formData.weight) : null,
      marital_status: formData.marital_status !== '' ? (Number(formData.marital_status) as any) : null,
      education: formData.education !== '' ? (Number(formData.education) as any) : null,
      occupation: formData.occupation !== '' ? String(formData.occupation) : null,
      religion: formData.religion !== '' ? (Number(formData.religion) as any) : null,
      sex: formData.sex !== '' ? (Number(formData.sex) as any) : null,
    };

    onSave(payload);
  };

  return (
    <Modal show onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{lan === 1 ? 'Edit Details' : 'Editar Detalles'}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Form.Group controlId="height" className="mb-3">
            <Form.Label>{VocabGeneralDisplay.height[lan]}</Form.Label>
            <Form.Control
              type="number"
              value={formData.height}
              onChange={(e) => handleChange('height', e.target.value)}
            />
          </Form.Group>

          <Form.Group controlId="weight" className="mb-3">
            <Form.Label>{VocabGeneralDisplay.weight[lan]}</Form.Label>
            <Form.Control
              type="number"
              value={formData.weight}
              onChange={(e) => handleChange('weight', e.target.value)}
            />
          </Form.Group>

          <Form.Group controlId="marital_status" className="mb-3">
            <Form.Label>{VocabGeneralDisplay.marital_status[lan]}</Form.Label>
            <Form.Select
              value={formData.marital_status}
              onChange={(e) => handleChange('marital_status', e.target.value)}
            >
              <option value="">--</option>
              <option value={0}>{VocabCivilStatus.single[lan]}</option>
              <option value={1}>{VocabCivilStatus.married[lan]}</option>
            </Form.Select>
          </Form.Group>

          <Form.Group controlId="education" className="mb-3">
            <Form.Label>{VocabGeneralDisplay.education[lan]}</Form.Label>
            <Form.Select
              value={formData.education}
              onChange={(e) => handleChange('education', e.target.value)}
            >
              <option value="">--</option>
              <option value={0}>{VocabQualifications.profesional[lan]}</option>
              <option value={1}>{VocabQualifications.masters[lan]}</option>
              <option value={2}>{VocabQualifications.doctorate[lan]}</option>
            </Form.Select>
          </Form.Group>

          <Form.Group controlId="occupation" className="mb-3">
            <Form.Label>{VocabGeneralDisplay.occupation[lan]}</Form.Label>
            <Form.Control
              type="text"
              value={formData.occupation}
              onChange={(e) => handleChange('occupation', e.target.value)}
            />
          </Form.Group>

          <Form.Group controlId="religion" className="mb-3">
            <Form.Label>{VocabGeneralDisplay.religion[lan]}</Form.Label>
            <Form.Select
              value={formData.religion}
              onChange={(e) => handleChange('religion', e.target.value)}
            >
              <option value="">--</option>
              <option value={0}>{VocabReligions.christianism[lan]}</option>
              <option value={1}>{VocabReligions.judaism[lan]}</option>
              <option value={2}>{VocabReligions.agnosticism[lan]}</option>
            </Form.Select>
          </Form.Group>

          <Form.Group controlId="sex" className="mb-3">
            <Form.Label>{VocabGeneralDisplay.sex[lan]}</Form.Label>
            <Form.Select
              value={formData.sex}
              onChange={(e) => handleChange('sex', e.target.value)}
            >
              <option value="">--</option>
              <option value={0}>{VocabSexes.female[lan]}</option>
              <option value={1}>{VocabSexes.male[lan]}</option>
            </Form.Select>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="danger" onClick={onHide}>
          {btn_vocab.cancel[lan]}
        </Button>
        <Button variant="primary" onClick={handleSave}>
          {btn_vocab.save[lan]}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}